import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconUserPlus } from '@tabler/icons-react';
import {
  computeShipmentEstimate,
  VAT_RATE_OPTIONS,
  type CarrierDto,
  type ClientType,
  type ShipmentLegDto,
  type ShipmentLegInput,
  type TransportMode,
} from '@expeditor/shared';
import { shipmentsApi, carriersApi } from '../../shared/api/shipments';
import {
  ROUTE_POINT_KIND_LABEL,
  TRANSPORT_MODE_LABEL,
} from '../../shared/ui/order-labels';

const MODE_OPTIONS = ['AUTO', 'RAIL', 'SEA', 'INLAND_WATER', 'AIR'].map((value) => ({
  value,
  label: TRANSPORT_MODE_LABEL[value] ?? value,
}));
const POINT_KIND_OPTIONS = Object.entries(ROUTE_POINT_KIND_LABEL).map(([value, label]) => ({
  value,
  label,
}));
const CARRIER_TYPE_OPTIONS = [
  { value: 'LEGAL_RU', label: 'ЮЛ РФ' },
  { value: 'ENTREPRENEUR', label: 'ИП' },
  { value: 'INDIVIDUAL', label: 'ФЛ' },
  { value: 'LEGAL_FOREIGN', label: 'Иностранная' },
];

type LegForm = {
  sequence: number;
  transportMode: TransportMode;
  fromKind: string;
  fromName: string;
  fromCountry: string;
  toKind: string;
  toName: string;
  toCountry: string;
  dateFrom: string;
  dateTo: string;
  carrierId: string | null;
  price: number | '';
  priceVatRate: string | null;
  comment: string;
};

const emptyForm = (seq: number): LegForm => ({
  sequence: seq,
  transportMode: 'AUTO',
  fromKind: 'SHIPPER',
  fromName: '',
  fromCountry: '',
  toKind: 'CONSIGNEE',
  toName: '',
  toCountry: '',
  dateFrom: '',
  dateTo: '',
  carrierId: null,
  price: '',
  priceVatRate: '20',
  comment: '',
});

function toForm(l: ShipmentLegDto): LegForm {
  return {
    sequence: l.sequence,
    transportMode: l.transportMode,
    fromKind: l.fromKind,
    fromName: l.fromName,
    fromCountry: l.fromCountry ?? '',
    toKind: l.toKind,
    toName: l.toName,
    toCountry: l.toCountry ?? '',
    dateFrom: l.dateFrom ?? '',
    dateTo: l.dateTo ?? '',
    carrierId: l.carrierId ?? null,
    price: l.price ?? '',
    priceVatRate: l.priceVatRate ?? '20',
    comment: l.comment ?? '',
  };
}

function toInput(f: LegForm): ShipmentLegInput {
  return {
    sequence: f.sequence,
    transportMode: f.transportMode,
    fromKind: f.fromKind as ShipmentLegInput['fromKind'],
    fromName: f.fromName,
    fromCountry: f.fromCountry || undefined,
    toKind: f.toKind as ShipmentLegInput['toKind'],
    toName: f.toName,
    toCountry: f.toCountry || undefined,
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
    carrierId: f.carrierId,
    price: f.price === '' ? null : Number(f.price),
    priceVatRate: f.priceVatRate,
    comment: f.comment || undefined,
  };
}

/**
 * Плечи перевозки (фаза 2): участок маршрута + исполнитель + себестоимость.
 * Объединяет «плечи» и «привлекаемых исполнителей». Редактируют менеджер/админ.
 */
export function ShipmentLegsSection({
  shipmentId,
  editable,
  onChanged,
}: {
  shipmentId: string;
  editable: boolean;
  /** Вызывается после изменения плеч — чтобы родитель обновил карточку перевозки. */
  onChanged?: () => void;
}) {
  const [legs, setLegs] = useState<ShipmentLegDto[]>([]);
  const [carriers, setCarriers] = useState<CarrierDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<LegForm>(emptyForm(1));
  const [busy, setBusy] = useState(false);
  // Быстрое добавление нового исполнителя прямо из формы плеча.
  const [ncOpen, setNcOpen] = useState(false);
  const [ncName, setNcName] = useState('');
  const [ncType, setNcType] = useState<ClientType>('LEGAL_RU');
  const [ncInn, setNcInn] = useState('');
  const [ncVat, setNcVat] = useState<string | null>('20');
  const [ncBusy, setNcBusy] = useState(false);

  const reload = () =>
    shipmentsApi
      .listLegs(shipmentId)
      .then(setLegs)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить плечи'));

  useEffect(() => {
    void reload();
    carriersApi.list().then(setCarriers).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const carrierOptions = carriers.map((c) => ({
    value: c.id,
    label: c.shortName || c.fullName,
  }));

  const resetNewCarrier = () => {
    setNcOpen(false);
    setNcName('');
    setNcType('LEGAL_RU');
    setNcInn('');
    setNcVat('20');
  };

  /** Создать новый привлекаемый исполнитель и сразу выбрать его исполнителем плеча. */
  const onCreateCarrier = async () => {
    if (!ncName.trim()) {
      setError('Укажите наименование нового исполнителя');
      return;
    }
    setNcBusy(true);
    setError(null);
    try {
      const created = await carriersApi.create({
        type: ncType,
        role: 'CARRIER',
        fullName: ncName.trim(),
        inn: ncInn.trim() || undefined,
        vatRate: ncVat ?? undefined,
      });
      setCarriers((prev) => [created, ...prev]);
      setForm((f) => ({
        ...f,
        carrierId: created.id,
        // Если у исполнителя задана ставка НДС — подставим её и в себестоимость плеча.
        priceVatRate: created.vatRate ?? f.priceVatRate,
      }));
      resetNewCarrier();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать исполнителя');
    } finally {
      setNcBusy(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(legs.length + 1));
    resetNewCarrier();
    setFormOpen(true);
  };
  const openEdit = (l: ShipmentLegDto) => {
    setEditingId(l.id);
    setForm(toForm(l));
    resetNewCarrier();
    setFormOpen(true);
  };

  const onSave = async () => {
    if (!form.fromName.trim() || !form.toName.trim()) {
      setError('Заполните пункты отправления и назначения');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editingId) {
        await shipmentsApi.updateLeg(shipmentId, editingId, toInput(form));
      } else {
        await shipmentsApi.createLeg(shipmentId, toInput(form));
      }
      setFormOpen(false);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить плечо');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (l: ShipmentLegDto) => {
    if (!confirm(`Удалить плечо ${l.sequence} (${l.fromName} → ${l.toName})?`)) return;
    setError(null);
    try {
      await shipmentsApi.removeLeg(shipmentId, l.id);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить плечо');
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={4}>Плечи перевозки</Title>
          <Text size="xs" c="dimmed">
            Участок маршрута + исполнитель + себестоимость
          </Text>
        </div>
        {editable && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Добавить плечо
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {legs.length === 0 ? (
        <Text c="dimmed" size="sm">
          Плечи ещё не заданы. Добавьте участки маршрута и исполнителей.
        </Text>
      ) : (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 32 }}>#</Table.Th>
              <Table.Th>Маршрут</Table.Th>
              <Table.Th>Вид</Table.Th>
              <Table.Th>Исполнитель</Table.Th>
              <Table.Th>Себестоимость</Table.Th>
              <Table.Th>Документы</Table.Th>
              {editable && <Table.Th style={{ width: 90 }} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {legs.map((l) => (
              <Table.Tr key={l.id}>
                <Table.Td>{l.sequence}</Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {l.fromName} → {l.toName}
                  </Text>
                </Table.Td>
                <Table.Td>{TRANSPORT_MODE_LABEL[l.transportMode] ?? l.transportMode}</Table.Td>
                <Table.Td>{l.carrierName ?? <Text c="dimmed">—</Text>}</Table.Td>
                <Table.Td>
                  {l.price != null ? (
                    <Text size="sm">
                      {l.price.toLocaleString('ru-RU')} {l.priceCurrency ?? 'RUB'}
                      {l.priceVatRate && l.priceVatRate !== '-1'
                        ? ` · НДС ${l.priceVatRate}%`
                        : l.priceVatRate === '-1'
                          ? ' · без НДС'
                          : ''}
                    </Text>
                  ) : (
                    <Text c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {l.documentNumbers.length > 0 ? (
                    <Badge variant="light">{l.documentNumbers.length}</Badge>
                  ) : (
                    <Text c="dimmed" size="xs">
                      нет
                    </Text>
                  )}
                </Table.Td>
                {editable && (
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() => openEdit(l)}
                        leftSection={<IconEdit size={14} />}
                      >
                        Правка
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        onClick={() => onDelete(l)}
                        leftSection={<IconTrash size={14} />}
                      />
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {legs.length > 0 && <EstimateSummary legs={legs} />}

      <Modal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Правка плеча' : 'Новое плечо'}
        size="lg"
      >
        <Stack>
          <Group grow>
            <NumberInput
              label="Порядок"
              min={1}
              value={form.sequence}
              onChange={(v) => setForm({ ...form, sequence: Number(v) || 1 })}
            />
            <Select
              label="Вид транспорта"
              data={MODE_OPTIONS}
              value={form.transportMode}
              onChange={(v) => v && setForm({ ...form, transportMode: v as TransportMode })}
            />
          </Group>
          <Group grow align="flex-start">
            <Stack gap="xs">
              <Select
                label="Тип точки отправления"
                data={POINT_KIND_OPTIONS}
                value={form.fromKind}
                onChange={(v) => v && setForm({ ...form, fromKind: v })}
              />
              <TextInput
                label="Пункт отправления"
                required
                value={form.fromName}
                onChange={(e) => setForm({ ...form, fromName: e.currentTarget.value })}
              />
              <TextInput
                label="Страна отправления"
                value={form.fromCountry}
                onChange={(e) => setForm({ ...form, fromCountry: e.currentTarget.value })}
              />
            </Stack>
            <Stack gap="xs">
              <Select
                label="Тип точки назначения"
                data={POINT_KIND_OPTIONS}
                value={form.toKind}
                onChange={(v) => v && setForm({ ...form, toKind: v })}
              />
              <TextInput
                label="Пункт назначения"
                required
                value={form.toName}
                onChange={(e) => setForm({ ...form, toName: e.currentTarget.value })}
              />
              <TextInput
                label="Страна назначения"
                value={form.toCountry}
                onChange={(e) => setForm({ ...form, toCountry: e.currentTarget.value })}
              />
            </Stack>
          </Group>
          <div>
            <Group justify="space-between" align="flex-end" gap="xs">
              <Select
                style={{ flex: 1 }}
                label="Исполнитель плеча"
                description="Кто везёт это плечо"
                data={carrierOptions}
                value={form.carrierId}
                onChange={(v) => setForm({ ...form, carrierId: v })}
                clearable
                searchable
                placeholder="не выбран"
              />
              <Button
                variant="light"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => setNcOpen((o) => !o)}
              >
                Новый исполнитель
              </Button>
            </Group>
            <Collapse in={ncOpen}>
              <Paper withBorder p="sm" radius="md" mt="xs">
                <Text size="sm" fw={500} mb="xs">
                  Новый привлекаемый исполнитель
                </Text>
                <Stack gap="xs">
                  <TextInput
                    label="Наименование"
                    required
                    value={ncName}
                    onChange={(e) => setNcName(e.currentTarget.value)}
                    placeholder="ООО «Перевозчик»"
                  />
                  <Group grow>
                    <Select
                      label="Тип"
                      data={CARRIER_TYPE_OPTIONS}
                      value={ncType}
                      onChange={(v) => v && setNcType(v as ClientType)}
                    />
                    <TextInput
                      label="ИНН"
                      value={ncInn}
                      onChange={(e) => setNcInn(e.currentTarget.value)}
                    />
                    <Select
                      label="Ставка НДС"
                      data={VAT_RATE_OPTIONS}
                      value={ncVat}
                      onChange={setNcVat}
                    />
                  </Group>
                  <Group justify="flex-end">
                    <Button variant="subtle" size="xs" onClick={resetNewCarrier}>
                      Отмена
                    </Button>
                    <Button size="xs" onClick={onCreateCarrier} loading={ncBusy}>
                      Создать и выбрать
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Остальные реквизиты (адрес, ЭДО, контакты) можно дозаполнить позже в
                    справочнике «Привлекаемые исполнители».
                  </Text>
                </Stack>
              </Paper>
            </Collapse>
          </div>
          <Group grow>
            <NumberInput
              label="Себестоимость (что платим исполнителю)"
              placeholder="0.00"
              min={0}
              decimalScale={2}
              thousandSeparator=" "
              value={form.price}
              onChange={(v) => setForm({ ...form, price: v === '' ? '' : Number(v) })}
            />
            <Select
              label="Ставка НДС"
              data={VAT_RATE_OPTIONS}
              value={form.priceVatRate}
              onChange={(v) => setForm({ ...form, priceVatRate: v })}
            />
          </Group>
          <TextInput
            label="Комментарий"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.currentTarget.value })}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setFormOpen(false)}>
              Отмена
            </Button>
            <Button onClick={onSave} loading={busy}>
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

const money = (n: number) =>
  n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Смета перевозки — внутренняя себестоимость (агрегат по плечам). */
function EstimateSummary({ legs }: { legs: ShipmentLegDto[] }) {
  const est = computeShipmentEstimate(legs);
  return (
    <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-gray-0)">
      <Group justify="space-between" mb={est.lines.length > 0 ? 6 : 0}>
        <Text fw={600} size="sm">
          Смета (себестоимость)
        </Text>
        <Text size="xs" c="dimmed">
          что платим исполнителям · {est.pricedLegs} из {est.totalLegs} плеч
        </Text>
      </Group>
      {est.lines.length === 0 ? (
        <Text size="sm" c="dimmed">
          Себестоимость плеч не указана.
        </Text>
      ) : (
        <Stack gap={2}>
          {est.lines.map((l) => (
            <Group key={l.currency} justify="space-between">
              <Text size="sm" fw={500}>
                {money(l.gross)} {l.currency}
              </Text>
              <Text size="xs" c="dimmed">
                в т.ч. НДС {money(l.vat)} · без НДС {money(l.net)}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
      {est.unpricedLegs > 0 && est.pricedLegs > 0 && (
        <Text size="xs" c="orange" mt={4}>
          {est.unpricedLegs} плеч без себестоимости — смета неполная.
        </Text>
      )}
    </Paper>
  );
}
